import { TELEMETRY_CLICK, type TelemetryEvent } from './events';
import { pushEvent } from './pushEvent';

/** Marks an element as a tracked interaction; its value is the unique GA4 key. */
export const TELEMETRY_NAME_ATTR = 'data-telemetry-name';
/** Logical grouping the element belongs to (e.g. `orders_view`). Required by convention (S5); optional at runtime. */
export const TELEMETRY_SECTION_ATTR = 'data-telemetry-section';
/** Human-readable label for the interaction. Required by convention (S5); optional at runtime. */
export const TELEMETRY_LABEL_ATTR = 'data-telemetry-label';
/** Optional dynamic identifier (order id, product id) resolved at click time. */
export const TELEMETRY_ID_ATTR = 'data-telemetry-id';

let activeDispose: (() => void) | undefined;

function handleClick(e: MouseEvent): void {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const el = target.closest(`[${TELEMETRY_NAME_ATTR}]`);
  if (!el) return;

  const name = el.getAttribute(TELEMETRY_NAME_ATTR);
  if (!name) return;

  // Emit every optional key on every event (undefined when the attribute is absent).
  // GTM's data model persists variables within a page, so omitting a key would let a
  // previous click's value bleed into this event; an explicit undefined overwrites it.
  const event: TelemetryEvent = {
    event: TELEMETRY_CLICK,
    action: 'click',
    name,
    section: el.getAttribute(TELEMETRY_SECTION_ATTR) || undefined,
    label: el.getAttribute(TELEMETRY_LABEL_ATTR) || undefined,
    id: el.getAttribute(TELEMETRY_ID_ATTR) || undefined,
  };

  pushEvent(event);
}

/**
 * Installs a single delegated `click` listener on `document` that turns any click on
 * (or within) an element carrying `data-telemetry-name` into exactly one normalized
 * `telemetry_click` dataLayer event.
 *
 * Attribute-driven so no per-element wiring is needed: authors tag markup with
 * `data-telemetry-name` (unique key) plus `-section` and `-label` (required by the S5
 * convention, though read defensively here) and optional `-id`. Prefer the `telemetryProps()`
 * helper, which enforces the required attributes at compile time.
 *
 * Idempotent: repeated calls reuse the single existing listener rather than binding
 * again. Returns a disposer that removes the listener and allows re-initialization.
 * No-op (returns a disposer that does nothing) when `document` is unavailable (SSR).
 */
export function initClickTracking(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (activeDispose) return activeDispose;

  // Capture phase: fires on the document->target descent, before any bubble-phase
  // `event.stopPropagation()` a component calls (common in grid rows/drawers to stop a
  // row/parent from also handling the click). A bubble-phase listener here would be
  // skipped for those clicks under React 17+, whose synthetic `stopPropagation` halts the
  // native event at the root container before it reaches `document`. This mirrors GTM's
  // own capture-phase click trigger.
  document.addEventListener('click', handleClick, true);

  const dispose = (): void => {
    document.removeEventListener('click', handleClick, true);
    if (activeDispose === dispose) activeDispose = undefined;
  };

  activeDispose = dispose;
  return dispose;
}
